import { FEATURE_LABELS, PLANS, type PlanFeature } from '@keel/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, MailWarning, Repeat, Sparkles, Users, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { errorMessage, useTRPC } from '@/api/query';
import { BRAND } from '@/brand';
import { Button, DialogContent, Dialog, toast } from '@/components/ui';
import { track } from '@/lib/analytics';
import { isBillingNotConfigured, useUpgrade } from '@/lib/plan';
import { useSession } from '@/lib/session';
import { useAppStore } from '@/lib/store';

const dismissed = (key: string): boolean => { try { return sessionStorage.getItem(key) === '1'; } catch { return false; } };
const dismiss = (key: string): void => { try { sessionStorage.setItem(key, '1'); } catch { /* private mode */ } };
const daysUntil = (iso: string | null | undefined): number => (iso ? Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000)) : 0);

const Banner = ({ children, onDismiss, tone = 'primary', label }: {
  readonly children: ReactNode; readonly onDismiss: () => void; readonly tone?: 'primary' | 'warning'; readonly label: string;
}) => (
  <div role="region" aria-label={label} data-print-hide
    className={tone === 'primary' ? 'border-b border-primary/20 bg-primary-soft' : 'border-b border-warning/30 bg-warning-soft'}>
    <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2 text-sm text-text sm:px-6 lg:px-8">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">{children}</div>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-surface hover:text-text">
        <X className="size-4" aria-hidden />
      </button>
    </div>
  </div>
);

/** Guests: which persona they are exploring, with Save (free) and Switch persona; dismissible per visit. */
export const GuestBanner = () => {
  const trpc = useTRPC();
  const { user } = useSession();
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(() => dismissed('hy:guest-banner'));
  const country = typeof user?.onboardingState.country === 'string' ? user.onboardingState.country : 'AU';
  const personaId = typeof user?.onboardingState.persona === 'string' ? user.onboardingState.persona : null;
  const personas = useQuery(trpc.auth.personas.queryOptions({ country: country === 'GB' ? 'UK' : country as 'AU' | 'US' | 'UK' }, { enabled: Boolean(user?.isGuest), staleTime: Infinity, meta: { silent: true } }));
  if (!user?.isGuest || hidden || pathname === '/signup') return null;
  const days = daysUntil(user.guestExpiresAt);
  const title = personas.data?.find((p) => p.id === personaId)?.title;
  return (
    <Banner label="Sample workspace" onDismiss={() => { dismiss('hy:guest-banner'); setHidden(true); }}>
      <Sparkles className="size-4 shrink-0 text-primary" aria-hidden />
      <span className="font-medium" data-testid="persona-banner">You’re exploring {title ? <strong>{title}</strong> : 'sample data'}</span>
      <span className="text-text-muted">Sample data, kept for {days} more day{days === 1 ? '' : 's'}.</span>
      <Button asChild size="sm"><Link to="/signup">Save my workspace</Link></Button>
      <Button asChild size="sm" variant="outline"><Link to={`/start?country=${country === 'GB' ? 'UK' : country}`}><Repeat className="size-4" aria-hidden />Switch persona</Link></Button>
    </Banner>
  );
};

/** Signed-up users with an unconfirmed email: a gentle reminder (only bank/HMRC connections and reset need it). */
export const VerifyEmailBanner = () => {
  const trpc = useTRPC();
  const { user } = useSession();
  const [hidden, setHidden] = useState(() => dismissed('hy:verify-banner'));
  const resend = useMutation(trpc.auth.resendVerification.mutationOptions({
    onSuccess: (r) => toast.success(r.alreadyVerified ? 'Your email is already confirmed.' : `Sent — check ${user?.email ?? 'your inbox'}.`),
  }));
  if (!user || user.isGuest || user.emailVerifiedAt || hidden) return null;
  return (
    <Banner tone="warning" label="Confirm your email" onDismiss={() => { dismiss('hy:verify-banner'); setHidden(true); }}>
      <MailWarning className="size-4 shrink-0 text-warning" aria-hidden />
      <span>Confirm <strong>{user.email}</strong> when you have a moment — you’ll need it to reset your password or connect a bank.</span>
      <Button variant="ghost" size="sm" loading={resend.isPending} onClick={() => resend.mutate()}>Resend link</Button>
    </Banner>
  );
};

const ROLE_AS: Record<string, string> = { owner: 'an owner', editor: 'an editor', viewer: 'a viewer', accountant: 'their accountant' };

/** Invitations to shared books for the signed-in (confirmed) address: nothing is added until they accept. */
export const InvitesBanner = () => {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { user } = useSession();
  const setEntity = useAppStore((s) => s.setEntity);
  const navigate = useNavigate();
  const q = useQuery(trpc.entities.myInvites.queryOptions(undefined, { meta: { silent: true }, staleTime: 5 * 60_000, enabled: Boolean(user && !user.isGuest && user.emailVerifiedAt) }));
  const respond = useMutation(trpc.entities.respondToInvite.mutationOptions({
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: trpc.entities.myInvites.queryKey() });
      if (!r.entityId) return;
      void qc.invalidateQueries({ queryKey: trpc.auth.me.queryKey() });
      setEntity(r.entityId);
      toast.success('You’re in — switched to the shared books.');
      void navigate('/');
    },
  }));
  const [hidden, setHidden] = useState(() => dismissed('hy:invite-banner'));
  const invite = q.data?.invites[0];
  if (!invite || hidden) return null;
  return (
    <Banner label="Invitation" onDismiss={() => { dismiss('hy:invite-banner'); setHidden(true); }}>
      <Users className="size-4 shrink-0 text-primary" aria-hidden />
      <span>{invite.invitedBy ? <strong>{invite.invitedBy}</strong> : 'Someone'} invited you to <strong>{invite.entityName}</strong> as {ROLE_AS[invite.role]}.</span>
      <Button size="sm" loading={respond.isPending} onClick={() => respond.mutate({ id: invite.id, accept: true })}>Accept</Button>
      <Button size="sm" variant="outline" disabled={respond.isPending} onClick={() => respond.mutate({ id: invite.id, accept: false })}>Decline</Button>
    </Banner>
  );
};

/** Days left of the Investor trial, in the top bar. */
export const TrialChip = () => {
  const trpc = useTRPC();
  const { user } = useSession();
  const q = useQuery(trpc.billing.status.queryOptions(undefined, { meta: { silent: true }, staleTime: 5 * 60_000, enabled: Boolean(user && !user.isGuest) }));
  if (!q.data?.onTrial) return null;
  const d = q.data.trialDaysLeft;
  return (
    <Link to="/settings/billing" data-testid="trial-chip"
      className="hidden h-8 items-center gap-1.5 rounded-full border border-primary/30 bg-primary-soft px-3 text-xs font-medium text-primary hover:border-primary sm:inline-flex">
      <Clock className="size-3.5" aria-hidden />
      Trial: {d} day{d === 1 ? '' : 's'} left
    </Link>
  );
};

const featureLabel = (f: string): string =>
  f === 'entities' ? 'More entities' : f === 'holdings' ? 'Unlimited holdings' : FEATURE_LABELS[f as PlanFeature] ?? 'This feature';

/** Opened by any PLAN_LIMIT error: what the plan adds, its price, and a way forward (never a dead end). */
export const UpgradeSheet = () => {
  const trpc = useTRPC();
  const { user } = useSession();
  const { need, close } = useUpgrade();
  const status = useQuery(trpc.billing.status.queryOptions(undefined, { meta: { silent: true }, enabled: Boolean(need && user) }));
  const [interval, setBilling] = useState<'annual' | 'monthly'>('monthly');
  const checkout = useMutation(trpc.billing.startCheckout.mutationOptions({
    meta: { silent: true },
    onSuccess: (r) => { window.location.href = r.url; },
    onError: (e) => { if (!isBillingNotConfigured(e)) toast.error(errorMessage(e)); },
  }));
  useEffect(() => { if (need) track('upgrade_sheet_view', { feature: need.feature, plan: need.requiredPlan }); }, [need]);
  if (!need) return null;
  const plan = PLANS[need.requiredPlan];
  const s = status.data;
  const price = s && need.requiredPlan !== 'free' ? s.prices[need.requiredPlan] : null;
  const sym = s?.currency === 'GBP' ? '£' : '$';
  const contact = s ? !s.configured || (checkout.error && isBillingNotConfigured(checkout.error)) : false;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        title={`${featureLabel(need.feature)} ${need.feature === 'entities' || need.feature === 'holdings' ? 'come' : 'comes'} with ${plan.name}`}
        description={need.message}
        footer={
          <>
            <Button variant="ghost" onClick={close}>Not now</Button>
            {user?.isGuest ? (
              <Button asChild onClick={close}><Link to="/signup">Save my workspace (free) first</Link></Button>
            ) : contact ? (
              <Button asChild><a href={`mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(`Upgrade to ${plan.name}`)}`}>Contact us to upgrade</a></Button>
            ) : (
              <Button loading={checkout.isPending} disabled={!s || need.requiredPlan === 'free'}
                onClick={() => need.requiredPlan !== 'free' && checkout.mutate({ plan: need.requiredPlan, interval })}>
                Upgrade to {plan.name}
              </Button>
            )}
          </>
        }
      >
        <ul className="flex flex-col gap-2 text-sm text-text">
          {plan.highlights.map((h) => (
            <li key={h} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-positive" aria-hidden />{h}</li>
          ))}
        </ul>
        {price ? (
          <div className="mt-4 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Billing interval">
            {(['monthly', 'annual'] as const).map((i) => (
              <button key={i} type="button" role="radio" aria-checked={interval === i} onClick={() => setBilling(i)}
                className={`rounded-md border px-3 py-2 text-sm ${interval === i ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text'}`}>
                {i === 'annual' ? `${sym}${price.annual}/year (save ${sym}${price.monthly * 12 - price.annual})` : `${sym}${price.monthly}/month`}
              </button>
            ))}
            {s?.foundingPrice && need.requiredPlan === 'investor' ? <span className="text-xs text-positive">Founding members: {sym}{s.foundingPrice}/year, locked in</span> : null}
          </div>
        ) : null}
        <p className="mt-4 text-xs text-text-muted">
          Nothing you’ve entered is locked or deleted — it stays readable on every plan. <Link to="/settings/billing" onClick={close} className="text-primary underline underline-offset-4">Compare plans</Link>
        </p>
      </DialogContent>
    </Dialog>
  );
};
